import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { searchContent, getSearchSuggestions, getTrendingSearches } from '../api/searchAPI';
import { useState, useEffect } from 'react';

// Hook for searching content
export const useSearch = (searchQuery, options = {}) => {
  const { setError } = useAuthStore();
  const queryClient = useQueryClient();
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  // Debounce the search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const enabled = debouncedQuery?.trim().length > 0;

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['search', debouncedQuery, options],
    () => searchContent(debouncedQuery, options),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Search failed';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      enabled: enabled,
      staleTime: 60000, // Consider data fresh for 1 minute
      cacheTime: 300000, // Keep data in cache for 5 minutes
      keepPreviousData: true, // Keep showing previous results while loading new ones
    }
  );

  // Prefetch the next page if we have data
  useEffect(() => {
    if (data?.meta?.page < data?.meta?.total_pages) {
      const nextPageOptions = { ...options, page: (options.page || 1) + 1 };
      queryClient.prefetchQuery(
        ['search', debouncedQuery, nextPageOptions],
        () => searchContent(debouncedQuery, nextPageOptions)
      );
    }
  }, [data, debouncedQuery, options, queryClient]);

  return {
    searchResults: data?.results || [],
    groupedResults: data?.grouped_results || { audios: [], ebooks: [], series: [] },
    meta: data?.meta || { total: 0, page: 1, limit: 20, total_pages: 0 },
    isLoading,
    error,
    refetch
  };
};

// Hook for getting search suggestions
export const useSearchSuggestions = (searchQuery, limit = 10) => {
  const { setError } = useAuthStore();
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  // Debounce the search query with shorter delay for suggestions
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const enabled = debouncedQuery?.trim().length > 1; // At least 2 characters for suggestions

  const {
    data: suggestions,
    isLoading,
    error
  } = useQuery(
    ['searchSuggestions', debouncedQuery, limit],
    () => getSearchSuggestions(debouncedQuery, limit),
    {
      onError: (error) => {
        console.error('Suggestions error:', error);
        const errorMessage = error?.response?.data?.error || 'Failed to get suggestions';
        setError(errorMessage);
      },
      enabled: enabled,
      staleTime: 30000, // Consider data fresh for 30 seconds
      cacheTime: 60000, // Keep data in cache for 1 minute
    }
  );

  return {
    suggestions: suggestions || [],
    isLoadingSuggestions: isLoading,
    suggestionsError: error
  };
};

// Hook for getting trending searches
export const useTrendingSearches = () => {
  const { setError } = useAuthStore();

  const {
    data: trendingSearches,
    isLoading,
    error,
    refetch
  } = useQuery(
    'trendingSearches',
    getTrendingSearches,
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch trending searches';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      staleTime: 3600000, // Consider data fresh for 1 hour
      cacheTime: 7200000, // Keep data in cache for 2 hours
    }
  );

  return {
    trendingSearches: trendingSearches || [],
    isLoadingTrending: isLoading,
    trendingError: error,
    refetchTrending: refetch
  };
};